import React, { useCallback, useEffect, useRef, useState } from "react";
import { X, Play, FilmStrip, ArrowsClockwise, WarningCircle, SpeakerSimpleHigh, ArrowsInLineHorizontal, PictureInPicture, ArrowsOut, ArrowsIn, FrameCorners, Sparkle, Microphone } from "@phosphor-icons/react";
import BackgroundPlayPill from "./BackgroundPlayPill";
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from "./ui/dialog";
import UniversalVideo from "./UniversalVideo";
import UpNextStrip from "./UpNextStrip";
import PlayerControls from "./PlayerControls";
// HolyWordsWatermark intentionally not imported — overlays removed per user request.
import AddToLineupButton from "./AddToLineupButton";
import CastButton from "./CastButton";
import { trackPlay } from "../lib/playTracker";
import { trackView, trackComplete } from "../lib/mediaAnalytics";
// MediaLikeButton intentionally not imported — TV screens kept icon-free per user request.
import CCToggleButton from "./CCToggleButton";
import CaptionSearchBox from "./CaptionSearchBox";
import { useCaptions } from "../context/CaptionsContext";
import useBackgroundPlayback from "../hooks/useBackgroundPlayback";
import useAutoPiP from "../hooks/useAutoPiP";
import useSwipeDownToMinimize from "../lib/useSwipeDownToMinimize";
import { useMiniPlayer } from "../context/MiniPlayerContext";
import { logWatch } from "../lib/history";
import JudgmentSection from "./JudgmentSection";
import SplashFlash from "./SplashFlash";
import CornerBrandMark from "./CornerBrandMark";
import CondensedPlayerStrip from "./CondensedPlayerStrip";
import useCondensedPlayer from "../hooks/useCondensedPlayer";
import useTheaterMode from "../hooks/useTheaterMode";
import { toast } from "sonner";
import { buildYouTubeEmbedSrc } from "../lib/ytPlayer";
import useYouTubeAutoAdvance from "../hooks/useYouTubeAutoAdvance";

// Loading shim while archive.org's video.js boots inside the iframe so
// users don't stare at a black square thinking the app is broken.
function ArchiveLoader({ title, poster }) {
  return (
    <div className="absolute inset-0 grid place-items-center pointer-events-none">
      {poster && (
        <img src={poster} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
      )}
      <div className="absolute inset-0 bg-ink/60" />
      <div className="relative text-cream text-center px-6 z-10">
        <div className="mx-auto w-12 h-12 rounded-full border-2 border-cream/40 border-t-cream animate-spin mb-3" />
        <div className="text-[11px] tracking-[0.25em] uppercase opacity-80">Loading from Internet Archive</div>
        <div className="text-sm opacity-80 mt-1 max-w-sm">{title || "Your video"} — first-time loads can take a few seconds on mobile.</div>
      </div>
    </div>
  );
}

// Tap-to-play poster + iframe + fallback — same proven pattern as the
// Music Video player. Why: archive.org's video.js shell takes 5–10s to
// boot inside the iframe; on slow networks the user sees a black box
// and assumes the app is broken. With this pattern the user sees the
// movie poster + a clear "Tap to play" button from the start, the
// iframe only mounts after they tap (gesture also satisfies autoplay
// policies), and if no playback within 12s we show a graceful "Open
// on Internet Archive" fallback button instead of a frozen black box.
function ArchiveFrame({ src, title, poster, autoStart = false }) {
  // When `autoStart` is true the parent already converted the user
  // gesture into "play" — skip the inner tap-to-play poster so the
  // user isn't asked to tap twice (the bug where Archive.org movies
  // needed 3 taps: open card → outer poster → inner poster).
  const [stage, setStage] = useState(autoStart ? "playing" : "poster");
  const [iframeLoaded, setIframeLoaded] = useState(false);
  // archiveId derived from `src` — needed for the "open in new tab" fallback.
  const detailsUrl = (() => {
    const m = src.match(/archive\.org\/embed\/([^/?&]+)/i);
    return m ? `https://archive.org/details/${m[1]}` : src;
  })();

  useEffect(() => {
    if (stage !== "playing") return;
    // Defensive: hide the loader after 30s even if iframe.onLoad never
    // fires, so the user can interact with the underlying iframe.
    const t = setTimeout(() => setIframeLoaded(true), 30000);
    return () => clearTimeout(t);
  }, [stage]);

  if (stage === "poster") {
    return (
      <button
        type="button"
        onClick={() => setStage("playing")}
        data-testid="archive-tap-to-play"
        className="absolute inset-0 grid place-items-center group"
      >
        {poster && (
          <img src={poster} alt={title || ""} className="absolute inset-0 w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/40 to-ink/25" />
        <div className="relative z-10 grid place-items-center gap-3 text-center px-6">
          <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-cream/95 grid place-items-center shadow-2xl group-hover:scale-105 transition-transform">
            <svg viewBox="0 0 24 24" className="w-8 h-8 md:w-10 md:h-10 text-forest fill-current ml-1"><path d="M8 5v14l11-7z"/></svg>
          </div>
          <div className="text-cream font-semibold text-base md:text-lg drop-shadow-lg max-w-md">{title}</div>
          <div className="text-cream/80 text-[10px] tracking-[0.2em] uppercase">Tap to play · Internet Archive</div>
        </div>
      </button>
    );
  }

  if (stage === "failed") {
    return (
      <div className="absolute inset-0 grid place-items-center bg-forest text-cream text-center px-6">
        {poster && (
          <img src={poster} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        )}
        <div className="relative z-10 max-w-md">
          <div className="text-[10px] tracking-[0.25em] uppercase opacity-70 mb-2">Playback hiccup</div>
          <div className="font-heading text-xl md:text-2xl mb-3">{title}</div>
          <p className="text-sm opacity-80 mb-5">
            This player took a little long to start. Open it on Internet Archive — same free
            public-domain film, full controls, plays everywhere.
          </p>
          <a
            href={detailsUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="archive-fallback-link"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-cream text-forest font-semibold text-sm hover:bg-cream/90"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current"><path d="M8 5v14l11-7z"/></svg>
            Watch on Internet Archive
          </a>
        </div>
      </div>
    );
  }

  return (
    <>
      <iframe
        src={src}
        title={title}
        allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
        allowFullScreen
        onLoad={() => {
          // iframe shell loaded → keep showing the loading hint for ~1s
          // then drop it. The hint is part of the loader overlay below.
        }}
        data-testid="archive-iframe"
        className="w-full h-full border-0"
      />
      {/* Block-clicks corner masks — Internet Archive paints their
          own clickable icons (library / share / "rate" / link to
          details page) into the corners of every embed. Those links
          jump users out of our app into archive.org and sometimes
          surface adjacent material we don't want kids tapping into.
          We can't style the cross-origin iframe, but we CAN cover
          its corners with opaque pointer-event-catching divs that
          eat the clicks. The video underneath still plays — only
          their UI chrome is blocked. */}
      <div
        aria-hidden="true"
        data-testid="archive-corner-mask-tr"
        className="absolute top-0 right-0 w-16 h-12 bg-transparent z-[5]"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
      />
      <div
        aria-hidden="true"
        data-testid="archive-corner-mask-br"
        className="absolute bottom-0 right-0 w-20 h-10 bg-transparent z-[5]"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
      />
      <ArchiveLoader title={title} poster={poster} />
    </>
  );
}

/** InAppVideoPlayer — visible YouTube player for cartoons, music videos,
 *  sermons-with-camera, etc.
 *
 *  Stage machine:
 *    poster   → waiting for tap-to-play
 *    playing  → iframe mounted
 *    failed   → YouTube reported the video unavailable / embed disabled,
 *               OR no playback within 7s. Friendly fallback panel shown
 *               with a Watch-on-YouTube button (never a black screen).
 */
export default function InAppVideoPlayer({ open, onClose, item, queryHint, accent = "terracotta", deadId = false, onNext, queue, onSelect, bucket, autoStart = false, startAt = null }) {
  const { minimize, close: closeMini } = useMiniPlayer();
  const { on: ccOn } = useCaptions();
  // Whenever the FULL InAppVideoPlayer opens with a NEW item, we
  // close the mini-player ONCE. Two competing iframes/videos on the
  // same page = double audio + the collapsed mini-tab gets stuck
  // behind the modal. The user explicitly reported: "I'm watching a
  // video and the mini player is stuck in the wall not coming out
  // playing something different".
  //
  // CRITICAL FIX (iter 92): track which item.id we already closed for
  // and never re-fire on close transitions. The previous version's
  // dep array `[open, item?.id, closeMini]` was technically guarded
  // by `if (open && item?.id)`, but when handoff timing meant the
  // user tapped "Mini Player" the same tick the effect was scheduled,
  // there were edge cases where closeMini ran AFTER minimize(), killing
  // the freshly-handed-off floating player. The ref guarantees strict
  // one-shot-per-item semantics.
  const closedForItemRef = useRef(null);
  useEffect(() => {
    if (!open) {
      closedForItemRef.current = null;
      return;
    }
    if (item?.id && closedForItemRef.current !== item.id) {
      closedForItemRef.current = item.id;
      try { closeMini(); } catch (_) {}
      // Cross-context audio bus — broadcast that a new playable
      // surface is opening so the floating BackgroundMusicPlayer (and
      // any other audio source) pauses itself. Eliminates the echo
      // users hear when a sermon opens while the home page's
      // background mix is still playing.
      try { window.dispatchEvent(new Event("hw:audio:start")); } catch (_) {}
    }
  }, [open, item?.id, closeMini]);
  // Log one play event per (bucket, item) per session for My Worship Mix.
  useEffect(() => {
    if (!open || !item) return;
    trackPlay(bucket, item);
    trackView(bucket, item);
  }, [open, item?.id, bucket]);
  // Auto-fade the top-bar chrome (CC / minimize / close) after 2.5s
  // of no taps — matches YouTube. Tap the player area to reveal
  // again. User asked: "the close caption button should not be seen
  // until you touch the screen". Same applies to minimize + close.
  const [chromeVisible, setChromeVisible] = useState(false);
  const chromeTimerRef = useRef(null);
  const showChrome = useCallback(() => {
    setChromeVisible(true);
    if (chromeTimerRef.current) clearTimeout(chromeTimerRef.current);
    chromeTimerRef.current = setTimeout(() => setChromeVisible(false), 2500);
  }, []);
  useEffect(() => {
    if (!open) return;
    // Start HIDDEN — user explicitly asked "nothing on the screen
    // and if they push the screen and tap it then CC, fast forward,
    // play, return comes up". Tapping the player area will run
    // showChrome and reveal everything for 2.5s.
    setChromeVisible(false);
    return () => { if (chromeTimerRef.current) clearTimeout(chromeTimerRef.current); };
  }, [open, item?.id]);
  // Idempotent minimize handler. The BackgroundPlayPill button used
  // to listen on both pointerdown AND click which fired the handler
  // twice in a single user gesture; we removed the pointerdown handler
  // in the pill, but this ref is still good belt-and-suspenders insurance
  // against rapid re-taps or synthetic events double-firing the handoff.
  const minimizingRef = useRef(false);
  const handleMinimize = () => {
    if (minimizingRef.current) return;
    minimizingRef.current = true;
    setTimeout(() => { minimizingRef.current = false; }, 500);
    // Capture the current playback position so the mini-player can
    // resume from EXACTLY where the user is, not restart from zero.
    // Tries the self-hosted <video> first (the most common case),
    // then falls back to the YouTube iframe's polled position.
    let resumeAt = 0;
    try {
      const v = cloudinaryVideoRef.current;
      if (v && Number.isFinite(v.currentTime) && v.currentTime > 1) {
        resumeAt = v.currentTime;
      } else {
        // YouTube leaves ytTimeRef.position at 0 for many embeds, so use
        // the same robust wall-clock playhead the lock-screen widget uses
        // (playheadRef) and take whichever is further along. Without this
        // the mini-player restarted YouTube tracks from the beginning.
        const yt = Math.max(playheadRef.current || 0, ytTimeRef.current?.position || 0);
        if (yt > 1) resumeAt = yt;
      }
    } catch (_) {}
    minimize({ ...item, resume_at: resumeAt }, queueRef.current);
    onClose && onClose();
  };

  // Swipe-down-to-minimize gesture — attached to the stage so swiping on
  // the video itself shrinks it to the floating mini-player while leaving
  // the up-next / comments scroll area below untouched.
  const swipe = useSwipeDownToMinimize(handleMinimize, { enabled: open });
  const [stage, setStage] = useState("poster"); // poster | playing | failed
  const [mode, setMode] = useState("direct"); // direct | search
  const [muted, setMuted] = useState(true);
  // Persist whether the user has ever tapped "enable sound" on this
  // device. Once they have, future plays attempt to start UNmuted —
  // both by setting `mute=0` in the iframe src (browsers may still
  // override) and by firing the unMute postMessage the moment the
  // iframe reports onReady. The user-gesture window from the tile
  // tap stays open just long enough on most browsers for this to
  // succeed silently on the second+ video. Eliminates the "tap to
  // enable sound" friction entirely after the first video.
  const soundPersistedOn = (() => {
    if (typeof window === "undefined") return false;
    try { return window.localStorage.getItem("hw:video:sound-on") === "1"; }
    catch (_) { return false; }
  })();
  const [posterOk, setPosterOk] = useState(true);
  const iframeRef = useRef(null);
  // Stage ref — used as the fullscreen target so we don't fullscreen
  // the whole modal (which would clip the iframe inside its own
  // sticky container) and instead promote the actual video element
  // / iframe to the screen.
  const stageRef = useRef(null);
  const [isFs, setIsFs] = useState(false);

  // ── Condensed-player mode ────────────────────────────────────────
  // When the user scrolls past the pinned video frame into the
  // Judgments thread, shrink the player to a thin "Now playing" strip
  // so the comments get the full screen. Tap the strip to expand.
  const scrollRef = useRef(null);
  const { condensed, expand } = useCondensedPlayer(scrollRef);
  // Theater mode — F to toggle, Esc to exit. Auto-exits on unmount
  // so the user can't get stuck without chrome after closing the player.
  const { theaterMode, toggle: toggleTheater, exit: exitTheater } = useTheaterMode();
  useEffect(() => () => exitTheater(), [exitTheater]);

  // Track native fullscreen state so the toggle button can flip its
  // icon + the stage can drop its `max-h-[60vh]` cap while fullscreen.
  useEffect(() => {
    const onChange = () => {
      setIsFs(document.fullscreenElement === stageRef.current || document.webkitFullscreenElement === stageRef.current);
    };
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
    };
  }, []);

  const toggleFullscreen = useCallback(async () => {
    const el = stageRef.current;
    if (!el) return;
    try {
      const isCurrentlyFs = document.fullscreenElement || document.webkitFullscreenElement;
      if (isCurrentlyFs) {
        const exit = document.exitFullscreen || document.webkitExitFullscreen;
        if (exit) await exit.call(document);
      } else {
        const req = el.requestFullscreen || el.webkitRequestFullscreen;
        if (req) await req.call(el);
      }
    } catch (_) {
      // iOS Safari throws if the user denies the request — silently ignore.
    }
  }, []);
  // See AudioVisualizerPlayer.jsx for the rationale: skip the tap-to-play
  // poster when chaining auto-play-next so consecutive videos roll
  // straight through like a real playlist.
  const autoStartRef = useRef(false);
  // Keep the most recent onNext in a ref so the postMessage handler
  // doesn't capture a stale closure when the parent re-renders.
  const onNextRef = useRef(onNext);
  useEffect(() => { onNextRef.current = onNext; }, [onNext]);
  // Shuffle / loop / queue refs — mirrored from AudioVisualizerPlayer.
  const [shuffleOn, setShuffleOn] = useState(false);
  // Default = "all" so the queue plays CONTINUOUSLY out of the box.
  // The user reported videos stopping at the end on production despite
  // our auto-advance fixes — defaulting to "all" makes the wrap-to-
  // start behavior the contract, not a fallback that depends on a
  // YouTube `ended` event that sometimes never fires (Vevo overlays,
  // mid-roll ads, region-blocked embeds, etc.). Tapping the Repeat
  // button now cycles: all → one → off → all, matching the new label
  // ordering in PlayerControls.
  const [loopMode, setLoopMode] = useState("all"); // all | one | off
  const [replayKey, setReplayKey] = useState(0);
  const shuffleHistoryRef = useRef([]);
  const queueRef = useRef(queue);
  useEffect(() => { queueRef.current = queue; }, [queue]);
  const onSelectRef = useRef(onSelect);
  useEffect(() => { onSelectRef.current = onSelect; }, [onSelect]);
  const itemIdRef = useRef(item?.id);
  useEffect(() => { itemIdRef.current = item?.id; }, [item?.id]);

  // Ref to the Cloudinary <video> element (set via forwardRef on
  // UniversalVideo). Powers the manual Picture-in-Picture button and
  // the auto-pop-out behavior on mobile tab-hide.
  const cloudinaryVideoRef = useRef(null);
  const pip = useAutoPiP({ videoRef: cloudinaryVideoRef, active: open && !!item?.cloudinary_url });

  // Deep-link seek-on-open. When the player opens via a "share this
  // exact moment" URL (e.g. `/sermons?play=<id>&t=347`), the parent
  // grid passes `startAt` in seconds. We watch for the video element
  // to load enough metadata that `currentTime = X` will actually stick,
  // then jump there and resume playback. Guarded by a ref so we only
  // apply the seek ONCE per item (subsequent shuffles / next-track
  // calls should NOT jump to that bookmark).
  const seekAppliedRef = useRef(false);
  useEffect(() => {
    // New item or player just opened → allow one more seek apply.
    seekAppliedRef.current = false;
  }, [item?.id, open]);
  useEffect(() => {
    if (!open || startAt == null || startAt <= 0) return;
    const v = cloudinaryVideoRef.current;
    if (!v) return;
    if (seekAppliedRef.current) return;
    const apply = () => {
      if (seekAppliedRef.current) return;
      try {
        v.currentTime = Math.max(0, Number(startAt) - 0.5);
        v.play?.();
        seekAppliedRef.current = true;
      } catch (_) { /* will retry on next event */ }
    };
    if (v.readyState >= 1) {
      apply();
    } else {
      v.addEventListener("loadedmetadata", apply, { once: true });
      v.addEventListener("canplay", apply, { once: true });
      return () => {
        v.removeEventListener("loadedmetadata", apply);
        v.removeEventListener("canplay", apply);
      };
    }
  }, [open, startAt, item?.id]);

  // End-of-track signals (YouTube state=0, near-end fallback, onError,
  // Cloudinary <video> onEnded) all route through
  // `handleAdvanceRef.current("auto")` so the SAME advance logic
  // governs every code path. `handleAdvance` already handles loop-one
  // (replay current track), shuffle (random pick from queue minus
  // recently played), and wrap-to-start — and reads its queue / item
  // through stable refs (`queueRef`, `itemIdRef`, `onSelectRef`) so
  // no stale-closure problems. Defined further below.

  // Real-playback gate + playhead/position tracking are owned by the
  // shared useYouTubeAutoAdvance hook (wired further below).

  const handleAdvance = React.useCallback((direction) => {
    // Pause auto-advance while the user is writing a judgment (comment)
    // so the thread doesn't reload out from under them. Manual prev/next
    // is unaffected.
    if (direction === "auto" && typeof window !== "undefined" && window.__hwCommenting) return false;
    const list = queueRef.current || [];
    const currentId = itemIdRef.current;
    if (direction === "auto" && loopMode === "one") {
      setReplayKey((k) => k + 1);
      return true;
    }
    if (list.length === 0) return false;
    const curIdx = list.findIndex((t) => t.id === currentId);

    let target = null;
    if (shuffleOn && direction !== "prev") {
      const recent = new Set([currentId, ...shuffleHistoryRef.current.slice(-Math.min(8, list.length - 1))]);
      const pool = list.filter((t) => !recent.has(t.id));
      const choice = pool.length ? pool : list.filter((t) => t.id !== currentId);
      if (choice.length) target = choice[Math.floor(Math.random() * choice.length)];
    } else if (direction === "prev") {
      if (curIdx > 0) target = list[curIdx - 1];
      else if (loopMode === "all" || direction === "auto") target = list[list.length - 1];
    } else {
      if (curIdx >= 0 && curIdx < list.length - 1) target = list[curIdx + 1];
      // "Continuous" mode (default loopMode === "off") wraps the queue
      // back to the start on auto-advance, so music videos / comedy /
      // cartoons keep rolling forever instead of stopping on the last
      // clip. Manual Next still respects "off" and closes when explicitly
      // pressed past the end (loopMode === "all" required for that).
      else if (loopMode === "all" || direction === "auto") target = list[0];
    }

    if (target) {
      if (shuffleOn) {
        shuffleHistoryRef.current = [...shuffleHistoryRef.current.slice(-7), currentId].filter(Boolean);
      }
      autoStartRef.current = true;
      if (typeof onSelectRef.current === "function") {
        setTimeout(() => onSelectRef.current(target), 200);
      }
      return true;
    }
    if (direction === "auto" && typeof onNextRef.current === "function") {
      setTimeout(() => onNextRef.current && onNextRef.current(), 200);
    }
    return false;
  }, [loopMode, shuffleOn]);
  const handleAdvanceRef = useRef(handleAdvance);
  useEffect(() => { handleAdvanceRef.current = handleAdvance; }, [handleAdvance]);

  // Keyboard navigation while the player is open. Adds the standard
  // YouTube-style shortcut set so users on a laptop / external
  // keyboard can drive the queue and seek without reaching for the
  // mouse:
  //   ↑ / Shift+→  → next video in the queue
  //   ↓ / Shift+←  → previous video in the queue
  //   ← / →        → seek -5s / +5s (Cloudinary self-hosted branch
  //                  only; the YouTube iframe handles its own
  //                  ←/→ shortcuts internally and we don't intercept).
  //   Space        → play/pause (Cloudinary branch)
  //   Esc          → close
  // We ignore key events from inputs / textareas so the chapter
  // search and Judgments comment box keep their normal typing
  // behavior.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      const t = e.target;
      const tag = (t && t.tagName) || "";
      if (tag === "INPUT" || tag === "TEXTAREA" || (t && t.isContentEditable)) return;
      switch (e.key) {
        case "ArrowUp":
          e.preventDefault();
          handleAdvanceRef.current && handleAdvanceRef.current("next");
          break;
        case "ArrowDown":
          e.preventDefault();
          handleAdvanceRef.current && handleAdvanceRef.current("prev");
          break;
        case "Escape":
          e.preventDefault();
          if (typeof onClose === "function") onClose();
          break;
        default:
          break;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  const handlePrev = () => handleAdvance("prev");
  const handleNextManual = () => handleAdvance("next");
  const handleToggleShuffle = () => setShuffleOn((v) => !v);
  // Cycle: all ↔ one. NO "off" option anymore — the user reported
  // accidentally cycling into "stop after this" mode and losing
  // continuous play. With only two states, every tap toggles
  // between Continuous (auto-advance + queue wrap) and Repeat
  // track (loop current song). Continuous can NEVER turn off.
  const handleCycleLoop = () => setLoopMode((m) => m === "all" ? "one" : "all");

  // Background playback: Screen Wake Lock + OS lock-screen / Bluetooth
  // / AirPods controls + auto-resume on tab-return. See
  // useBackgroundPlayback for details.
  //
  // We track currentTime/duration via YT `infoDelivery` postMessages so
  // fast-forward / rewind on the lock-screen widget can compute an
  // absolute seek target (YT iframe API only exposes seekTo, not seekBy).
  // Timestamp (performance.now) of the iframe's DOM `load` event —
  // reliable anchor for the playhead even when the YT IFrame API is
  // suppressed (FBR / Vevo).
  const iframeLoadedAtRef = useRef(0);
  const sendYt = useCallback((func, args = []) => {
    const fr = iframeRef.current;
    if (!fr || !fr.contentWindow) return;
    try {
      fr.contentWindow.postMessage(JSON.stringify({ event: "command", func, args }), "*");
    } catch (_) {}
  }, []);
  // Shared YouTube iframe auto-advance + auto-unmute detector. Owns the
  // near-end / catalog-timer / watchdog fallbacks and the position +
  // playhead refs that background playback and Pin-to-Trending read.
  const { ytTimeRef, playheadRef } = useYouTubeAutoAdvance({
    iframeRef,
    active: open && stage === "playing",
    trackKey: item?.id,
    durationText: item?.duration,
    bucket,
    onAdvance: () => handleAdvanceRef.current && handleAdvanceRef.current("auto"),
    onUnmute: () => setMuted(false),
    onError: () => setStage("failed"),
    iframeLoadedAtRef,
    options: { catalogTimer: true, wallClock: true, watchdog: true, playedGate: true, advanceOnError: true },
  });
  useBackgroundPlayback({
    active: open && stage === "playing",
    track: item,
    iframeRef,
    onNext: () => handleAdvance("next"),
    onPrev: () => handleAdvance("prev"),
    onPause: () => sendYt("pauseVideo"),
    onPlay: () => sendYt("playVideo"),
    onSeekForward: (delta) => {
      const { position, duration } = ytTimeRef.current;
      const target = duration > 0 ? Math.min(duration - 0.5, position + delta) : position + delta;
      sendYt("seekTo", [Math.max(0, target), true]);
    },
    onSeekBackward: (delta) => {
      const { position } = ytTimeRef.current;
      sendYt("seekTo", [Math.max(0, position - delta), true]);
    },
    onSeekTo: (seconds) => sendYt("seekTo", [Math.max(0, seconds), true]),
    getPositionState: () => ytTimeRef.current,
  });

  // Voice-control bus listener — see AudioVisualizerPlayer.jsx for the
  // full explanation. Allows "next song", "pause", etc. from anywhere.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      const verb = e?.detail?.verb;
      if (!verb) return;
      if (verb === "next") handleAdvance("next");
      else if (verb === "prev") handleAdvance("prev");
      else if (verb === "shuffle") setShuffleOn((v) => !v);
      else if (verb === "loop") setLoopMode((m) => m === "all" ? "one" : "all");
      else if (verb === "close") onClose && onClose();
      else if (verb === "pause") {
        const fr = iframeRef.current;
        if (fr && fr.contentWindow) {
          try { fr.contentWindow.postMessage(JSON.stringify({ event: "command", func: "pauseVideo", args: [] }), "*"); } catch (_) { /* noop */ }
        }
      } else if (verb === "resume") {
        const fr = iframeRef.current;
        if (fr && fr.contentWindow) {
          try { fr.contentWindow.postMessage(JSON.stringify({ event: "command", func: "playVideo", args: [] }), "*"); } catch (_) { /* noop */ }
        }
      }
    };
    window.addEventListener("hw-voice-command", handler);
    return () => window.removeEventListener("hw-voice-command", handler);
  }, [open, onClose, handleAdvance]);

  useEffect(() => {
    if (!open) return;
    // Default to direct mode. Old listType=search was deprecated 2020-11-15.
    setMode("direct");
    // Default the muted state to OFF if the user has previously chosen
    // sound-on on this device. The iframe loads with `mute=0` in that
    // case (see directSrc), so we should match the state rather than
    // briefly flash a "tap to enable sound" pill before the auto-unmute
    // effect runs.
    setMuted(!soundPersistedOn);
    setPosterOk(true);
    // Skip the tap-to-play poster entirely. The tile-click that opened
    // this modal already counts as the user gesture mobile autoplay
    // policy requires — going straight to "playing" eliminates the
    // "tap once, then tap again" complaint. Muted autoplay starts the
    // video, and the aggressive auto-unmute loop (in the effect below)
    // brings sound on within ~1-2s. Cleaner UX, fewer pushes.
    setStage("playing");
    autoStartRef.current = false;
    // Log to unified watch history (silent no-op for anon users).
    // `bucket` distinguishes videos / kids / sermons / lessons /
    // gospel_music / christian_rap / choir / music_videos so the
    // History page can route the user back to the right page.
    if (item?.id) {
      const subtitle = item.artist || item.speaker || item.teacher || item.story || item.category || "";
      const thumbnail = item.poster || (item.youtube_id ? `https://i.ytimg.com/vi/${item.youtube_id}/hqdefault.jpg` : "");
      logWatch({
        id: item.id,
        type: bucket || (item.story ? "video" : item.artist ? "music" : item.speaker ? "sermon" : "video"),
        title: item.title,
        subtitle,
        thumbnail,
      });
    }
  }, [open, item?.id, deadId, autoStart, bucket]);

  // Lock body scroll while open. Use a simple overflow:hidden — not
  // the body{position:fixed; top:-scrollY} trick because it would
  // displace the body and break the position:fixed modal itself.
  useEffect(() => {
    if (!open) return;
    const prev = {
      bodyOverflow: document.body.style.overflow,
      htmlOverflow: document.documentElement.style.overflow,
      bodyTouchAction: document.body.style.touchAction,
    };
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    document.body.style.touchAction = "none";
    return () => {
      document.body.style.overflow = prev.bodyOverflow;
      document.documentElement.style.overflow = prev.htmlOverflow;
      document.body.style.touchAction = prev.bodyTouchAction;
    };
  }, [open]);

  const handleUnmute = () => {
    const fr = iframeRef.current;
    if (!fr || !fr.contentWindow) return;
    try {
      fr.contentWindow.postMessage(JSON.stringify({ event: "command", func: "unMute", args: [] }), "*");
      fr.contentWindow.postMessage(JSON.stringify({ event: "command", func: "setVolume", args: [100] }), "*");
      fr.contentWindow.postMessage(JSON.stringify({ event: "command", func: "playVideo", args: [] }), "*");
    } catch (_) {}
    setMuted(false);
    // Remember the user wants sound on so future plays skip the
    // friction. See `soundPersistedOn` near the top of this component.
    try {
      if (typeof window !== "undefined") {
        window.localStorage.setItem("hw:video:sound-on", "1");
      }
    } catch (_) {}
  };

  // Remember when the user explicitly mutes too — so we don't keep
  // re-unmuting them on subsequent videos against their wishes.
  const handleMute = () => {
    const fr = iframeRef.current;
    try {
      if (fr && fr.contentWindow) {
        fr.contentWindow.postMessage(JSON.stringify({ event: "command", func: "mute", args: [] }), "*");
      }
    } catch (_) {}
    try {
      const v = cloudinaryVideoRef?.current;
      if (v) v.muted = true;
    } catch (_) {}
    setMuted(true);
    try {
      if (typeof window !== "undefined") {
        window.localStorage.removeItem("hw:video:sound-on");
      }
    } catch (_) {}
  };


  // ── Persistent-iframe state for continuous play ──────────────────
  // Declared above any early return (rules-of-hooks).
  const stableSrcRef = useRef(null);
  const lastLoadedIdRef = useRef(null);
  useEffect(() => {
    if (!open) {
      stableSrcRef.current = null;
      lastLoadedIdRef.current = null;
    }
  }, [open]);
  useEffect(() => {
    if (!open || stage !== "playing") return;
    const ytId = item?.youtube_id;
    if (!ytId) return;
    if (lastLoadedIdRef.current === null) {
      lastLoadedIdRef.current = ytId;
      return;
    }
    if (lastLoadedIdRef.current === ytId) return;
    const t = setTimeout(() => {
      const fr = iframeRef.current;
      if (!fr || !fr.contentWindow) return;
      try {
        fr.contentWindow.postMessage(JSON.stringify({
          event: "command",
          func: "loadVideoById",
          args: [{ videoId: ytId, startSeconds: 0 }],
        }), "*");
        // Player started this session muted; once the user has
        // engaged we attempt unMute right after the swap so the
        // chained song plays out loud without another tap.
        fr.contentWindow.postMessage(JSON.stringify({ event: "command", func: "unMute", args: [] }), "*");
        fr.contentWindow.postMessage(JSON.stringify({ event: "command", func: "setVolume", args: [100] }), "*");
        lastLoadedIdRef.current = ytId;
      } catch (_) { /* noop */ }
    }, 200);
    return () => clearTimeout(t);
  }, [item?.youtube_id, open, stage]);
  useEffect(() => {
    if (replayKey === 0) return;
    const fr = iframeRef.current;
    if (!fr || !fr.contentWindow) return;
    try {
      fr.contentWindow.postMessage(JSON.stringify({ event: "command", func: "seekTo", args: [0, true] }), "*");
      fr.contentWindow.postMessage(JSON.stringify({ event: "command", func: "playVideo", args: [] }), "*");
    } catch (_) { /* noop */ }
  }, [replayKey]);

  if (!item) return null;

  // Self-hosted media — render the native HTML5 video for true Cloudinary
  // uploads, or an archive.org embed iframe when the source is a
  // public-domain title from archive.org. Their player handles all
  // codec/streaming so it works in every browser; raw .ia.mp4 URLs
  // sometimes fail decoder support with MEDIA_ERR_SRC_NOT_SUPPORTED.
  if (item.cloudinary_url) {
    const cu = item.cloudinary_url;
    const archiveMatch = cu.match(/archive\.org\/(?:download|details|embed)\/([^/]+)/i);
    const trackMatch = cu.match(/#archive-track=(\d+)/);
    const trackParam = trackMatch ? `&start=${trackMatch[1]}` : "";
    const archiveEmbedSrc = archiveMatch
      ? `https://archive.org/embed/${archiveMatch[1]}?autoplay=1&playsinline=1${trackParam}`
      : null;
    return (
      <Dialog open={open} onOpenChange={(o) => !o && onClose && onClose()}>
        <DialogContent className="w-full max-w-screen-2xl pc:!max-w-none pc:rounded-none p-0 overflow-hidden sm:rounded-3xl rounded-none border-none bg-black" data-testid="in-app-video-player">
          <DialogTitle className="sr-only">{item.title}</DialogTitle>
          <DialogDescription className="sr-only">{item.artist || item.story || ""}</DialogDescription>
          {/* Floating ALWAYS-VISIBLE close — sits on top of the modal
              so the shadcn-tiny corner X is never the only escape. */}
          <button
            type="button"
            onClick={() => onClose && onClose()}
            data-testid="video-close-floating"
            aria-label="Close player"
            title="Close (Esc)"
            className="absolute top-2 right-2 z-[50] w-7 h-7 grid place-items-center rounded-full bg-ink/60 hover:bg-ink/85 text-cream backdrop-blur-sm active:scale-95 transition-all"
          >
            <X size={14} weight="bold" />
          </button>
          <div ref={scrollRef} className="flex flex-col max-h-[100dvh] overflow-y-auto md:max-h-none md:overflow-visible pc:flex-row pc:max-h-screen pc:overflow-hidden" onPointerDown={showChrome}>
            {/* LEFT COLUMN (laptop split-screen). On mobile/tablet this
                is a plain flex-col so the original stacked layout is
                untouched; on lg+ it becomes the 65% left pane with the
                video + controls pinned and judgments scrolling. */}
            <div className="flex flex-col min-w-0 pc:w-[52%] pc:max-h-screen pc:overflow-hidden">
            <div ref={stageRef} className="aspect-video pc:aspect-[16/10] bg-ink relative shrink-0 max-h-[85vh] md:max-h-[50vh] pc:max-h-[72vh] sticky top-0 z-20 md:static md:z-auto">
              {/* HW brand-mark watermark — doubles as tap-to-like. */}
              {/* Brand mark removed per user request — TV screens
                  should be free of icons. SplashFlash still plays
                  the "presents" moment when the player first opens. */}
              <SplashFlash itemId={`vid-${item.id}`} sub={item.title} />
              <CornerBrandMark />
              {archiveEmbedSrc ? (
                <ArchiveFrame
                  src={archiveEmbedSrc}
                  title={item.title}
                  poster={item.poster}
                  autoStart
                />
              ) : (
                <UniversalVideo
                  ref={cloudinaryVideoRef}
                  src={cu}
                  poster={item.poster}
                  autoPlay
                  onEnded={() => { trackComplete(bucket, item); handleAdvanceRef.current && handleAdvanceRef.current("auto"); }}
                  data-testid="cloudinary-video"
                  className="w-full h-full object-cover"
                  captionsUrl={ccOn && cu ? `/api/captions/vtt?url=${encodeURIComponent(cu)}` : null}
                />
              )}
              {/* Mini Player handoff moved to the toolbar BELOW the
                  video stage per user request — TV screen stays
                  icon-free. */}
            </div>
            {/* CONTROL PANEL — pinned (does not scroll) on laptop. */}
            <div className="pc:shrink-0 pc:mt-auto">
            <div
              className={`p-5 md:p-6 text-cream flex items-center gap-3 transition-opacity duration-300 pc:py-2.5 pc:px-0 ${chromeVisible ? "opacity-100" : "opacity-0 pointer-events-none"}`}
              onPointerDown={showChrome}
              onMouseMove={showChrome}
              data-testid="in-app-video-toolbar"
            >
              <div className="flex-1 min-w-0">
                {item.category && (
                  <div className={`text-[10px] tracking-[0.2em] uppercase text-${accent}`}>{item.category} · {archiveEmbedSrc ? "Public domain" : "Self-hosted"} · <span className="text-cream/55">HW · Holy Words</span></div>
                )}
                <h2 className="font-heading text-2xl md:text-3xl pc:text-lg truncate">{item.title}</h2>
                {(item.artist || item.speaker || item.teacher || item.story) && (
                  <p className="text-cream/70 text-sm mt-1 truncate">
                    {item.artist || item.speaker || item.teacher || item.story}
                    {item.duration ? ` · ${item.duration}` : ""}
                  </p>
                )}
              </div>
              {bucket && (
                <div className="shrink-0" data-testid="video-add-to-lineup">
                  <AddToLineupButton title={item.title} youtubeId={item.youtube_id || null} url={item.cloudinary_url || item.url || null} poster={item.poster || null} source={item.artist || item.speaker || "Video"} duration={item.duration || null} size={16} className="w-10 h-10 bg-cream/15 hover:bg-cream/25 text-cream border border-cream/15" />
                </div>
              )}
              {!archiveEmbedSrc && (
                <CastButton
                  media={{
                    url: cu,
                    contentType: "video/mp4",
                    title: item.title,
                    subtitle: item.artist || item.speaker || item.teacher || "",
                    poster: item.poster,
                  }}
                  videoRef={null}
                  testid="in-app-video-cast"
                  className="shrink-0"
                />
              )}
              <CCToggleButton variant="text" testid="in-app-video-cc-selfhosted" className="shrink-0" />
              <button
                type="button"
                onClick={toggleFullscreen}
                className="shrink-0 w-10 h-10 grid place-items-center rounded-full bg-cream/15 hover:bg-cream/25 text-cream"
                title={isFs ? "Exit fullscreen" : "Fullscreen"}
                aria-label={isFs ? "Exit fullscreen" : "Enter fullscreen"}
                aria-pressed={isFs ? "true" : "false"}
                data-testid="in-app-video-fullscreen-selfhosted"
              >
                {isFs ? <ArrowsIn size={16} weight="bold" /> : <ArrowsOut size={16} weight="bold" />}
              </button>
              {/* Sermon transcript search — appears as a tiny "Search in this"
                  pill in the header for self-hosted media (Cloudinary +
                  Archive.org). Captions must already be cached server-side
                  (auto-generated by Whisper on first play); when they are,
                  tapping a hit seeks the video to the exact second. */}
              {cu && (
                <CaptionSearchBox
                  mediaUrl={cu}
                  accent={accent}
                  shareItem={{ id: item.id, title: item.title }}
                  onSeek={(sec) => {
                    const v = cloudinaryVideoRef.current;
                    if (v) {
                      try { v.currentTime = Math.max(0, sec - 0.5); v.play?.(); } catch (_) {}
                    }
                  }}
                />
              )}
              {!archiveEmbedSrc && pip.supported && (
                <button
                  onClick={pip.requestPiP}
                  data-testid="in-app-video-pip"
                  aria-label={pip.inPiP ? "Exit picture in picture" : "Picture in picture"}
                  title={pip.inPiP ? "Exit floating window" : "Pop into a floating window — keeps playing when you leave the app"}
                  className={`shrink-0 inline-flex items-center gap-1.5 px-1 h-9 text-[11px] tracking-[0.18em] uppercase font-bold transition-all ${pip.inPiP ? "text-amber-300 hover:text-amber-200" : "text-cream/85 hover:text-cream"}`}
                >
                  <PictureInPicture size={14} weight="bold" />
                  <span>{pip.inPiP ? "PiP on" : "Picture in"}</span>
                </button>
              )}
              <button
                onClick={onClose}
                className="shrink-0 w-10 h-10 grid place-items-center rounded-full bg-cream/15 hover:bg-cream/25"
                data-testid="in-app-video-close"
              >
                <X size={16} weight="bold" />
              </button>
            </div>
            <PlayerControls
              shuffleOn={shuffleOn}
              loopMode={loopMode}
              onShuffle={handleToggleShuffle}
              onLoop={handleCycleLoop}
              onPrev={handlePrev}
              onNext={handleNextManual}
              canPrev={(queue?.length || 0) > 0}
              canNext={(queue?.length || 0) > 0}
              accent={accent}
              testidPrefix="video"
              bucket={bucket}
              queue={queue}
              shareItem={{
                id: item.id,
                title: item.title,
                subtitle: item.artist || item.speaker || item.teacher || item.story || "",
                getCurrentTime: () => cloudinaryVideoRef.current?.currentTime || 0,
                mediaUrl: cu || (item.youtube_id ? `https://youtu.be/${item.youtube_id}` : `hw:item:${item.id}`),
              }}
            />
            </div>
            {item?.id && (
              <div data-testid="video-judgments-cl" className="pc:hidden">
                <JudgmentSection
                  targetType={bucketToTarget(bucket)}
                  targetId={String(item.id)}
                  title={item.title || ""}
                  accent={accent}
                  variant="bare"
                />
              </div>
            )}
            {/* Up Next in-flow for mobile/tablet only (laptop shows it in the right column). */}
            <div className="pc:hidden">
              <UpNextStrip items={queue} currentId={item.id} onSelect={onSelect} accent={accent} />
            </div>
            </div>
            {/* RIGHT COLUMN (laptop split-screen) — Up Next, scrolls independently. */}
            <div className="hidden pc:flex pc:w-[48%] pc:max-h-screen pc:border-l pc:border-cream/10">
              <div className="w-[36%] shrink-0 overflow-y-auto border-r border-cream/10">
                <UpNextStrip items={queue} currentId={item.id} onSelect={onSelect} accent={accent} orientation="vertical" max={30} />
              </div>
              <div className="flex-1 min-w-0 overflow-y-auto">
                <JudgmentSection targetType={bucketToTarget(bucket)} targetId={String(item.id)} title={item.title || ""} accent={accent} scripture bibleOnly />
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const subtitle = item.artist || item.speaker || item.teacher || item.story || "";
  const searchTerms = queryHint || `${subtitle} ${item.title} ${item.category === "Music videos" ? "official" : ""}`.trim();
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  // Same continuous-play fix as AudioVisualizerPlayer: pin the iframe
  // src on first transition to "playing" and swap subsequent tracks
  // via YouTube's `loadVideoById` postMessage inside the SAME iframe.
  // Mounting a fresh iframe per track re-triggers the autoplay-policy
  // gate and was the root cause of "I have to tap play after every
  // song".
  const directSrc = item.youtube_id
    ? buildYouTubeEmbedSrc(item.youtube_id, { mute: soundPersistedOn ? 0 : 1, ccOn, origin })
    : null;
  if (stage === "playing" && !stableSrcRef.current && directSrc) {
    stableSrcRef.current = directSrc;
  }
  const src = stableSrcRef.current || directSrc;
  // Founder rule (Feb 2026): never leave the app — YouTube watch + search URLs removed.
  const poster = item.youtube_id ? `https://i.ytimg.com/vi/${item.youtube_id}/hqdefault.jpg` : null;

  const handleRetrySearch = () => {
    // Founder rule: never bounce users out of the app. Previously
    // this opened a YouTube search; we now treat "retry" as a no-op
    // toast so users stay in the app and can pick another title.
    try { toast.info("This video isn't available right now. Try another from the library."); } catch (_) {}
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose && onClose()}>
      <DialogContent className="w-full max-w-screen-2xl pc:!max-w-none pc:rounded-none p-0 overflow-hidden sm:rounded-3xl rounded-none border-none bg-black" data-testid="in-app-video-player">
        <DialogTitle className="sr-only">{item.title}</DialogTitle>
        <DialogDescription className="sr-only">{subtitle}</DialogDescription>
        {/* Floating ALWAYS-VISIBLE close — sits on top of the modal
            so the shadcn-tiny corner X is never the only escape. */}
        <button
          type="button"
          onClick={() => onClose && onClose()}
          data-testid="video-close-floating-yt"
          aria-label="Close player"
          title="Close (Esc)"
          className="absolute top-2 right-2 z-[50] w-7 h-7 grid place-items-center rounded-full bg-ink/60 hover:bg-ink/85 text-cream backdrop-blur-sm active:scale-95 transition-all"
        >
          <X size={14} weight="bold" />
        </button>
        <div ref={scrollRef} className="flex flex-col max-h-[98vh] overflow-y-auto pc:flex-row pc:max-h-screen pc:overflow-hidden">
          {/* LEFT COLUMN (laptop split) — video + controls pinned, judgments scroll. */}
          <div className="flex flex-col min-w-0 pc:w-[52%] pc:max-h-screen pc:overflow-hidden">
          <div
            ref={stageRef}
            onTouchStart={swipe.onTouchStart}
            onTouchMove={swipe.onTouchMove}
            onTouchEnd={swipe.onTouchEnd}
            data-testid="video-player-stage"
            className={`relative overflow-hidden w-full sticky top-0 z-20 shrink-0 mx-auto transition-[max-height,aspect-ratio] duration-300 ease-out ${
              isFs
                ? ""
                : condensed
                  ? "max-h-[68px] aspect-auto"
                  : "aspect-video pc:aspect-[16/10] max-h-[85vh] md:max-h-[50vh] pc:max-h-[72vh]"
            }`}
            style={{
              // 16:9 like YouTube — on laptop we cap height to 60vh so
              // the video stays big AND the up-next / comments show
              // below the fold. `shrink-0` keeps the parent flex from
              // squashing the stage. `mx-auto` centers the resulting
              // letterbox on ultra-wide monitors.
              //
              // When `condensed` is true (user scrolled into Judgments),
              // we drop the 16:9 ratio + cap max-height to 68px so the
              // player becomes a thin "Now playing" strip. The iframe
              // keeps playing (audio uninterrupted) and the
              // `CondensedPlayerStrip` below overlays it with a
              // tap-to-expand affordance.
              aspectRatio: isFs ? "auto" : condensed ? "auto" : undefined,
              // Solid near-black behind the iframe. Previously a
              // green→orange gradient that the founder called the
              // "back-splash" — it flashed for ~1s while the YouTube
              // iframe loaded and looked like a broken splash screen.
              // Black matches the rest of the player chrome so the
              // viewer sees the player chrome the same color before
              // and after the video starts.
              background: "#0a0a0a",
              ...swipe.style,
            }}
          >
            {condensed && !isFs && (
              <CondensedPlayerStrip
                title={item.title}
                subtitle={item.artist || item.speaker || ""}
                poster={item.poster || item.thumbnail}
                onExpand={expand}
                accent="cream"
                testid="video-condensed-strip"
              />
            )}
            {/* HW brand-mark watermark — doubles as tap-to-like. */}
            {/* Brand mark removed per user request — TV screens
                should be free of icons. */}
            <SplashFlash itemId={`vid-yt-${item.id}`} sub={item.title} />
            <CornerBrandMark />
            {stage === "poster" ? (
              <button
                onClick={() => {
                  setStage("playing");
                  // Tap = user gesture justifying sound on mobile. Auto-unmute
                  // as soon as the iframe is reachable to avoid the "tap
                  // twice to hear" UX.
                  let tries = 0;
                  const timer = setInterval(() => {
                    const fr = iframeRef.current;
                    tries += 1;
                    if (fr && fr.contentWindow) {
                      try {
                        fr.contentWindow.postMessage(JSON.stringify({ event: "command", func: "unMute", args: [] }), "*");
                        fr.contentWindow.postMessage(JSON.stringify({ event: "command", func: "setVolume", args: [100] }), "*");
                        fr.contentWindow.postMessage(JSON.stringify({ event: "command", func: "playVideo", args: [] }), "*");
                      } catch (_) { /* ignore */ }
                      setMuted(false);
                      clearInterval(timer);
                    } else if (tries > 20) {
                      clearInterval(timer);
                    }
                  }, 200);
                }}
                data-testid="video-play-overlay"
                className="absolute inset-0 w-full h-full group"
                aria-label={`Play ${item.title}`}
              >
                {poster && posterOk && (
                  <img src={poster} alt="" className="absolute inset-0 w-full h-full object-cover"
                       onError={() => setPosterOk(false)} />
                )}
                {(!poster || !posterOk) && (
                  <div className="absolute inset-0 grid place-items-center text-cream/90">
                    <div className="text-center max-w-md px-6">
                      <FilmStrip size={48} weight="duotone" className="mx-auto opacity-75" />
                      <div className="text-[10px] tracking-[0.3em] uppercase mt-3 opacity-80 font-semibold">{item.category || "Holy Words"}</div>
                      <h3 className="font-heading text-3xl mt-1 leading-tight">{item.title}</h3>
                    </div>
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/15 to-black/40" />
                <div className="absolute inset-0 grid place-items-center">
                  <div className={`w-20 h-20 md:w-24 md:h-24 rounded-full bg-${accent} text-cream grid place-items-center shadow-2xl group-hover:scale-110 transition-all`}>
                    <Play size={36} weight="fill" className="ml-1" />
                  </div>
                </div>
                <div className="absolute bottom-5 left-1/2 -translate-x-1/2 text-cream text-[11px] tracking-[0.25em] uppercase font-semibold opacity-90">
                  Tap to play with sound
                </div>
              </button>
            ) : stage === "playing" ? (
              src ? (
                <>
                  <iframe
                    ref={iframeRef}
                    key={`${mode}`}
                    title={item.title}
                    data-testid="in-app-video-iframe"
                    className="absolute inset-0 w-full h-full"
                    src={src}
                    onLoad={() => { iframeLoadedAtRef.current = performance.now(); }}
                    allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                    allowFullScreen
                  />
                  {/* Big centered "Tap to play with sound" overlay. The
                      video IS already playing — just muted — but the
                      tiny "tap to enable sound" pill in the footer was
                      so quiet that users perceived the whole thing as
                      broken (multiple reports: "thumbnails not
                      opening"). A huge pulsing overlay makes the call-
                      to-action unmissable, and dismissing it (or any
                      footer interaction) hands the iframe the user
                      gesture browsers need to honor unMute. Only renders
                      while the iframe is in muted-autoplay mode; gone
                      the instant onReady confirms unmuted. */}
                  {muted && (
                    <button
                      type="button"
                      onClick={handleUnmute}
                      data-testid="video-unmute-overlay"
                      aria-label="Tap to play with sound"
                      className="absolute inset-0 z-20 grid place-items-center bg-black/30 hover:bg-black/40 transition-colors"
                    >
                      <div className="flex flex-col items-center gap-3 pointer-events-none">
                        <div className="w-20 h-20 rounded-full bg-cream text-forest grid place-items-center shadow-2xl animate-pulse">
                          <SpeakerSimpleHigh size={36} weight="fill" />
                        </div>
                        <div className="text-cream text-xs tracking-[0.18em] uppercase font-bold drop-shadow-lg">
                          Tap to play with sound
                        </div>
                      </div>
                    </button>
                  )}
                  {/* Mini Player handoff moved OUT of the video
                      stage — now lives in the toolbar below. */}
                </>
              ) : (
                // No youtube_id resolved — show "this title isn't available
                // in-app yet" in-app (no external bounce).
                <div className="absolute inset-0 grid place-items-center text-cream p-6 text-center">
                  <div className="max-w-md">
                    <FilmStrip size={42} weight="duotone" className="mx-auto opacity-75" />
                    <h3 className="font-heading text-xl mt-3">Not available right now</h3>
                    <p className="text-cream/80 text-sm mt-2 leading-relaxed">
                      We don't have an in-app stream for this title yet. Try another from your library — we add new ones every week.
                    </p>
                  </div>
                </div>
              )
            ) : (
              // failed
              <div className="absolute inset-0 grid place-items-center text-cream p-6 text-center">
                <div className="max-w-md">
                  <WarningCircle size={42} weight="duotone" className="mx-auto text-amber-300" />
                  <h3 className="font-heading text-2xl mt-3">Couldn't start playback</h3>
                  <p className="text-cream/80 text-sm mt-2 leading-relaxed">
                    This video isn't available right now. Tap below to refresh or pick another title from your library.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2 justify-center mt-5">
                    <button
                      type="button"
                      onClick={handleRetrySearch}
                      data-testid="video-retry-search"
                      className="inline-flex items-center justify-center gap-2 px-2 h-11 text-cream hover:text-terracotta font-semibold tracking-wider uppercase text-xs transition-colors"
                    >
                      <ArrowsClockwise size={14} weight="bold" /> Try again
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="p-5 md:p-6 text-cream pc:shrink-0 pc:py-2.5 pc:px-0">
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0">
                {item.category && (
                  <div className={`text-[10px] tracking-[0.2em] uppercase text-${accent}`}>{item.category} · <span className="text-cream/55">HW · Holy Words</span></div>
                )}
                <h2 className="font-heading text-2xl md:text-3xl pc:text-lg truncate">{item.title}</h2>
                {subtitle && (
                  <p className="text-cream/70 text-sm mt-1 truncate">{subtitle}{item.duration ? ` · ${item.duration}` : ""}</p>
                )}
              </div>
              <div
                className={`flex items-center gap-3 transition-opacity duration-300 ${chromeVisible ? "opacity-100" : "opacity-0 pointer-events-none"}`}
                onPointerDown={showChrome}
                data-testid="in-app-video-yt-toolbar"
              >
                <CCToggleButton variant="text" testid="in-app-video-cc" className="shrink-0" />
                <button
                  type="button"
                  onClick={toggleFullscreen}
                  className="shrink-0 w-10 h-10 grid place-items-center rounded-full bg-cream/15 hover:bg-cream/25"
                  title={isFs ? "Exit fullscreen" : "Fullscreen"}
                  aria-label={isFs ? "Exit fullscreen" : "Enter fullscreen"}
                  aria-pressed={isFs ? "true" : "false"}
                  data-testid="in-app-video-fullscreen"
                >
                  {isFs ? <ArrowsIn size={16} weight="bold" /> : <ArrowsOut size={16} weight="bold" />}
                </button>
                <button
                  onClick={onClose}
                  className="shrink-0 w-10 h-10 grid place-items-center rounded-full bg-cream/15 hover:bg-cream/25"
                  title="Close"
                  data-testid="in-app-video-close"
                >
                  <X size={16} weight="bold" />
                </button>
              </div>
            </div>
            {/* Always-visible YouTube escape hatch — so even if the embed
                quietly fails to start, the user has a one-tap working path.
                The sound toggle lives here too because mobile WebView
                sometimes renders YouTube's iframe controls above any
                in-iframe overlay (so the in-iframe pill became
                un-tappable). Putting the unmute button OUTSIDE the
                iframe — in the modal footer — guarantees it's reachable. */}
            {stage === "playing" && (
              <div className="mt-4 flex flex-wrap gap-2 items-center">
                {muted ? (
                  <button
                    onClick={handleUnmute}
                    data-testid="video-unmute-footer"
                    className="inline-flex items-center gap-1.5 px-2 h-9 text-cream hover:text-cream/80 text-[11px] font-bold tracking-wider uppercase animate-pulse transition-colors"
                  >
                    <SpeakerSimpleHigh size={13} weight="fill" /> Tap to enable sound
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleMute}
                    data-testid="video-mute-yt"
                    title="Tap to mute"
                    className="inline-flex items-center gap-1.5 px-2 h-9 text-emerald-300 hover:text-cream text-[11px] tracking-wider uppercase font-semibold transition-colors"
                  >
                    <SpeakerSimpleHigh size={11} weight="fill" /> Sound on · tap to mute
                  </button>
                )}
              </div>
            )}
            <PlayerControls
              shuffleOn={shuffleOn}
              loopMode={loopMode}
              onShuffle={handleToggleShuffle}
              onLoop={handleCycleLoop}
              onPrev={handlePrev}
              onNext={handleNextManual}
              canPrev={(queue?.length || 0) > 0}
              canNext={(queue?.length || 0) > 0}
              accent={accent}
              testidPrefix="video"
              bucket={bucket}
              queue={queue}
              shareItem={{
                id: item.id,
                title: item.title,
                subtitle: subtitle,
                getCurrentTime: () => Math.max(playheadRef.current || 0, ytTimeRef.current?.position || 0),
                mediaUrl: item.youtube_id ? `https://youtu.be/${item.youtube_id}` : `hw:item:${item.id}`,
              }}
            />
            {/* Footer-row Comments button — duplicates the in-stage
                hint so laptop users (where YouTube's iframe chrome can
                cover the bottom-left corner) always have a labeled
                way to jump to community comments. */}
            <div className="flex flex-wrap items-center gap-2 mt-1">
              {/* Cast-to-TV pill — labeled, only renders when a Cast
                  device is detected on the network. */}
              <CastButton
                media={{
                  url: item.cloudinary_url || item.mp4_url,
                  contentType: "video/mp4",
                  title: item.title,
                  subtitle: subtitle,
                  poster: item.poster,
                  youtubeWatchUrl: null, // Founder rule: never leave the app.
                }}
                videoRef={cloudinaryVideoRef}
                testid="video-cast-pill"
                variant="pill"
              />
              {bucket && (
                <div data-testid="video-add-to-lineup-yt" className="shrink-0">
                  <AddToLineupButton title={item.title} youtubeId={item.youtube_id || null} url={item.cloudinary_url || item.url || null} poster={item.poster || null} source={item.artist || item.speaker || "Video"} duration={item.duration || null} size={16} className="w-10 h-10 bg-cream/15 hover:bg-cream/25 text-cream border border-cream/15" />
                </div>
              )}
              {item?.id && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      try {
                        const el = document.querySelector("[data-testid='video-judgments']");
                        el && el.scrollIntoView({ behavior: "smooth", block: "start" });
                      } catch (_) {}
                    }}
                    data-testid="video-comments-jump"
                    aria-label="Jump to judgments"
                    className="inline-flex items-center gap-1.5 px-2 h-10 text-xs tracking-wider uppercase font-bold text-forest hover:text-terracotta transition-colors"
                  >
                    Judgments
                  </button>
                  <button
                    type="button"
                    onClick={toggleTheater}
                    data-testid="video-theater-toggle"
                    aria-label={theaterMode ? "Exit theater mode (Esc)" : "Theater mode (F)"}
                    title={theaterMode ? "Exit theater mode (Esc)" : "Theater mode — hide chrome (F)"}
                    className="hidden pc:inline-flex items-center gap-1.5 px-2 h-10 text-xs tracking-wider uppercase font-bold text-forest hover:text-terracotta transition-colors"
                  >
                    <FrameCorners size={13} weight="bold" /> {theaterMode ? "Exit theater" : "Theater"}
                  </button>
                  <BackgroundPlayPill
                    onClick={handleMinimize}
                    testid="in-app-video-bg-pill"
                    className="shrink-0"
                  />
                  <button type="button" onClick={() => window.dispatchEvent(new CustomEvent("holywords:open-ai"))} data-testid="in-app-video-ai" aria-label="Ask AI" title="Ask AI" className="shrink-0 w-8 h-8 grid place-items-center text-[#C9A24E] hover:scale-110 transition-transform"><Sparkle size={15} weight="fill" /></button>
                  <button type="button" onClick={() => window.dispatchEvent(new CustomEvent("holywords:open-voice"))} data-testid="in-app-video-voice" aria-label="Voice commands" title="Voice commands" className="shrink-0 w-8 h-8 grid place-items-center text-terracotta hover:scale-110 transition-transform"><Microphone size={15} weight="fill" /></button>
                </>
              )}
            </div>
          </div>
            {item?.id && (
              <div data-testid="video-judgments" className="mt-0 px-5 md:px-6 pc:hidden">
                <JudgmentSection
                  targetType={bucketToTarget(bucket)}
                  targetId={String(item.id)}
                  title={item.title || ""}
                  accent={accent}
                  variant="bare"
                />
              </div>
            )}
            {/* Up Next in-flow for mobile/tablet only (laptop shows it in the right column). */}
            <div className="pc:hidden px-5 md:px-6">
              <UpNextStrip items={queue} currentId={item.id} onSelect={onSelect} accent={accent} />
            </div>
          </div>
            {/* RIGHT COLUMN (laptop split) — Up Next, scrolls independently. */}
            <div className="hidden pc:flex pc:w-[48%] pc:max-h-screen pc:border-l pc:border-cream/10">
              <div className="w-[36%] shrink-0 overflow-y-auto border-r border-cream/10">
                <UpNextStrip items={queue} currentId={item.id} onSelect={onSelect} accent={accent} orientation="vertical" max={30} />
              </div>
              <div className="flex-1 min-w-0 overflow-y-auto">
                <JudgmentSection targetType={bucketToTarget(bucket)} targetId={String(item.id)} title={item.title || ""} accent={accent} scripture bibleOnly />
              </div>
            </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Map the calling page's "bucket" (sermons / videos / gospel_music) to
// the target_type the judgment system understands. Keeps the comment
// thread cleanly scoped per content type without forcing the caller
// to think about backend taxonomy.
function bucketToTarget(b) {
  if (b === "sermons") return "sermon";
  if (b === "gospel_music" || b === "songs") return "song";
  // Comedy gets its own target type so stand-up comment threads don't
  // mix with the kids' Bible videos that also fall under the generic
  // "video" bucket.
  if (b === "comedy") return "comedy";
  return "video";
}
